/**
 * Chat Feedback Prompt Component
 * Displays a feedback prompt asking "Was this response helpful?" after assistant messages
 * when feedback collection is enabled.
 */

import React, { useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { ThumbUp, ThumbDown } from "@mui/icons-material";
import { useTranslucenceChatFeedbackCreateMutation } from "@app/api/carApi.manual";

export interface ChatFeedbackPromptProps {
  planResultRootId: string;
  planResultTypeId: string;
  requestId: string;
  query?: string;
  response?: string;
  onSubmitted?: () => void;
}

export const ChatFeedbackPrompt: React.FC<ChatFeedbackPromptProps> = ({
  planResultRootId,
  planResultTypeId,
  requestId,
  query,
  response,
  onSubmitted,
}) => {
  const [isSatisfied, setIsSatisfied] = useState<boolean | null>(null);
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [submitFeedback] = useTranslucenceChatFeedbackCreateMutation();

  const handleFeedback = async (satisfied: boolean) => {
    if (isSubmitted) return;

    setIsSatisfied(satisfied);
    
    // Auto-submit if satisfied (positive feedback)
    if (satisfied) {
      await handleSubmit(satisfied);
    }
  };

  const handleSubmit = async (satisfied?: boolean) => {
    if (isSubmitting || isSubmitted) return;

    const feedbackValue = satisfied !== undefined ? satisfied : isSatisfied;
    if (feedbackValue === null) return;

    setIsSubmitting(true);

    try {
      await submitFeedback({
        planResultRootId,
        planResultTypeId,
        requestId,
        isSatisfied: feedbackValue,
        comments: comments.trim() || undefined,
        query: query || undefined,
        response: response || undefined,
      }).unwrap();

      setIsSubmitted(true);
      onSubmitted?.();
      console.log("[Chat] Feedback submitted successfully:", {
        requestId,
        isSatisfied: feedbackValue,
      });
    } catch (error) {
      console.error("[Chat] Failed to submit feedback:", error);
      // Don't show error to user - just log it
      // The feedback prompt will remain visible so they can try again
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          bgcolor: "success.light",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: "success.dark" }}>
          Thank you for your feedback!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: "grey.100",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="par02Regular" sx={{ mb: 1.5, fontWeight: 500 }}>
        Was this response helpful?
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: isSatisfied === false ? 1.5 : 0 }}>
        <Button
          variant={isSatisfied === true ? "contained" : "outlined"}
          size="small"
          startIcon={<ThumbUp />}
          onClick={() => handleFeedback(true)}
          disabled={isSubmitting || isSubmitted}
          sx={{
            flex: 1,
            textTransform: "none",
          }}
        >
          Yes
        </Button>
        <Button
          variant={isSatisfied === false ? "contained" : "outlined"}
          color={isSatisfied === false ? "error" : "primary"}
          size="small"
          startIcon={<ThumbDown />}
          onClick={() => handleFeedback(false)}
          disabled={isSubmitting || isSubmitted}
          sx={{
            flex: 1,
            textTransform: "none",
          }}
        >
          No
        </Button>
      </Box>

      {isSatisfied === false && (
        <Box sx={{ mt: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="What could we improve? (optional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={isSubmitting || isSubmitted}
            sx={{ mb: 1 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => handleSubmit()}
            disabled={isSubmitting || isSubmitted}
            sx={{ textTransform: "none" }}
          >
            {isSubmitting ? "Submitting..." : "Submit Feedback"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

